import {
  Button,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Col,
} from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FeedbackSlider from "../common/FeedbackSlider";
import { feedbackValidationRules } from "../../utils/feedbackValidation";

const FormItemLayout = {
  labelCol: { xl: { span: 24 } },
  wrapperCol: { xl: { span: 24 } },
};

const initialFeedbackData = {
  email: "",
  orderNumber: "",
  recommendationRating: undefined,
  statusUpdatesRating: undefined,
  turnaroundTimeRating: undefined,
  complianceRating: undefined,
  accuracyRating: undefined,
  additionalComments: "",
  contactRepresentative: undefined,
  suitableDateFrom: undefined,
  suitableDateTo: undefined,
  suitableTime: "",
};

const FeedBackForm = ({ onFinish, loading, languageKey = "" }) => {
  const { t } = useTranslation();
  const [feedbackForm] = Form.useForm();
  const [feedbackData, setFeedbackData] = useState(initialFeedbackData);

  const contactWantsYes =
    Form.useWatch("contactRepresentative", feedbackForm) === true;
  const suitableDateFrom = Form.useWatch("suitableDateFrom", feedbackForm);

  const validationRules = feedbackValidationRules;

  const onValuesChange = (changedValues, allValues) => {
    if (
      Object.hasOwn(changedValues, "contactRepresentative") &&
      changedValues.contactRepresentative === false
    ) {
      feedbackForm.setFieldsValue({
        suitableDateFrom: undefined,
        suitableDateTo: undefined,
        suitableTime: undefined,
      });
    }
    if (Object.hasOwn(changedValues, "suitableDateFrom")) {
      const from = changedValues.suitableDateFrom;
      const to = allValues.suitableDateTo;
      if (from && to && dayjs.isDayjs(from) && dayjs.isDayjs(to) && to.isBefore(from, "day")) {
        feedbackForm.setFieldsValue({ suitableDateTo: undefined });
        setFeedbackData((prev) => ({ ...prev, ...allValues, suitableDateTo: undefined }));
        return;
      }
    }
    setFeedbackData((prev) => ({ ...prev, ...allValues }));
  };

  /** From Date: only today and future selectable; past dates disabled */
  const disabledDateFrom = (current) => {
    if (!current) return false;
    return current < dayjs().startOf("day");
  };

  /** To Date: only today and future; cannot be before today or before From Date */
  const disabledDateTo = (current) => {
    if (!current) return false;
    const todayStart = dayjs().startOf("day");
    if (current < todayStart) return true; // previous dates not selectable
    if (suitableDateFrom && dayjs.isDayjs(suitableDateFrom)) {
      return current < suitableDateFrom.startOf("day"); // must be >= From Date
    }
    return false;
  };

  return (
    <Form
      key={`feedback-${languageKey}`}
      {...FormItemLayout}
      form={feedbackForm}
      variant="outlined"
      onFinish={onFinish}
      onValuesChange={onValuesChange}
      initialValues={{
        email: feedbackData.email,
        orderNumber: feedbackData.orderNumber,
        recommendationRating: feedbackData.recommendationRating,
        statusUpdatesRating: feedbackData.statusUpdatesRating,
        turnaroundTimeRating: feedbackData.turnaroundTimeRating,
        complianceRating: feedbackData.complianceRating,
        accuracyRating: feedbackData.accuracyRating,
        additionalComments: feedbackData.additionalComments,
        contactRepresentative: feedbackData.contactRepresentative,
        suitableDateFrom: feedbackData.suitableDateFrom,
        suitableDateTo: feedbackData.suitableDateTo,
        suitableTime: feedbackData.suitableTime,
      }}
      colon={false}
      name="feedback"
      layout="vertical"
      className="flex flex-col gap-4"
    >
      <Row gutter={[20, 0]}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Email"
            name="email"
            required
            rules={validationRules.email}
          >
            <Input
              maxLength={100}
              placeholder="Enter your email"
              disabled={loading}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Order ID"
            name="orderNumber"
            rules={validationRules.orderNumber}
          >
            <Input
              maxLength={100}
              placeholder="Enter your order id"
              disabled={loading}
              onChange={(e) => {
                const digitsOnly = e.target.value.replaceAll(/\D/g, "");
                if (digitsOnly !== e.target.value) {
                  feedbackForm.setFieldsValue({ orderNumber: digitsOnly });
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label={
          "How likely is it that you would recommend Provider1st to a friend or colleague?"
        }
        name="recommendationRating"
        required
        rules={validationRules.recommendationRating}
      >
        <FeedbackSlider
          min={0}
          max={10}
          leftLabel="Not at all likely"
          rightLabel="Extremely likely"
          disabled={loading}
        />
      </Form.Item>

      <Form.Item
        label={
          "How satisfied are you with status updates and responsiveness?"
        }
        name="statusUpdatesRating"
        required
        rules={validationRules.statusUpdatesRating}
      >
        <FeedbackSlider
          min={0}
          max={5}
          leftLabel="Strongly disagree"
          rightLabel="Strongly agree"
          disabled={loading}
        />
      </Form.Item>

      <Form.Item
        label={
          "How satisfied are you with request turnaround time (receipt to fulfillment)?"
        }
        name="turnaroundTimeRating"
        required
        rules={validationRules.turnaroundTimeRating}
      >
        <FeedbackSlider
          min={0}
          max={5}
          leftLabel="Strongly disagree"
          rightLabel="Strongly agree"
          disabled={loading}
        />
      </Form.Item>

      <Form.Item
        label={
          "How confident are you that releases are handled compliantly (HIPAA, State rules, etc.)?"
        }
        name="complianceRating"
        required
        rules={validationRules.complianceRating}
      >
        <FeedbackSlider
          min={0}
          max={5}
          leftLabel="Strongly disagree"
          rightLabel="Strongly agree"
          disabled={loading}
        />
      </Form.Item>

      <Form.Item
        label={
          "How satisfied are you with the accuracy and completeness of records released?"
        }
        name="accuracyRating"
        required
        rules={validationRules.accuracyRating}
      >
        <FeedbackSlider
          min={0}
          max={5}
          leftLabel="Strongly disagree"
          rightLabel="Strongly agree"
          disabled={loading}
        />
      </Form.Item>

      <Form.Item
        label={
          "Is there anything else you'd like to share about your experience with Provider1st?"
        }
        name="additionalComments"
        required
        rules={validationRules.additionalComments}
      >
        <Input.TextArea
          rows={4}
          placeholder="Enter your feedback..."
          disabled={loading}
          maxLength={1000}
          showCount
        />
      </Form.Item>

      <div>
        <Form.Item
          label={
            "Would you like a representative from Provider1st to contact you to discuss your feedback or experience with our service?"
          }
          name="contactRepresentative"
          required
          rules={validationRules.contactRepresentative}
        >
          <Radio.Group disabled={loading}>
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>

        {contactWantsYes && (
          <>
            <div className=" mb-3 text-xs text-[#666666]">
              If yes, please provide the suitable time.
            </div>
            <Row gutter={[20, 0]}>
              <Col xs={24} md={8}>
                <Form.Item
                  label={"From Date"}
                  name="suitableDateFrom"
                  required
                  rules={validationRules.suitableDateFrom}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    disabled={loading}
                    format="MM/DD/YYYY"
                    disabledDate={disabledDateFrom}
                    placeholder="Select Date"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={"To Date"}
                  name="suitableDateTo"
                  required
                  rules={validationRules.suitableDateTo}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    disabled={loading || !suitableDateFrom}
                    format="MM/DD/YYYY"
                    disabledDate={disabledDateTo}
                    placeholder="Select Date"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={"Suitable Time"}
                  name="suitableTime"
                  required
                  rules={validationRules.suitableTime}
                >
                  <Input
                    placeholder="Enter suitable time"
                    disabled={loading}
                    maxLength={100}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </div>

      <Row>
        <Col span={24} className="text-right">
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={loading}
            >
              Submit
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

FeedBackForm.propTypes = {
  onFinish: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  languageKey: PropTypes.string,
};

export default FeedBackForm;
